import { useQuery } from '@apollo/client';
import { Box, Flex, HStack, VStack } from '@chakra-ui/react';
import { formatActivityType } from '@shinetools/ape-code-library';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import { ApplicantType } from 'features/Onboarding/types';

import Header from '../../../../components/Header';
import { PollVerificationModuleKybDocumentsDocument } from '../../graphql/queries/pollVerificationModuleKybDocuments.gql';
import getVerificationSteps from '../../lib/steps/getVerificationSteps';
import {
  VerificationStatus,
  VerificationStep,
  VerificationStepCategory,
} from '../../lib/steps/types';
import { State } from '../../machine';
import { VerificationModuleData } from '../../machine/model';
import Category from './components/Category';
import locales from './locales';

interface OnboardingHubProps {
  moduleData: VerificationModuleData;
  onStepClick: (step: VerificationStep) => void;
  onSubmitApplication: () => void;
  onBeneficiariesClick: () => void;
  onGeneralInformationClick: () => void;
}

const OnboardingHub = asBentoPage<OnboardingHubProps>(
  ({
    Layout,
    moduleData,
    onGeneralInformationClick,
    onPrev,
    onStepClick,
    onSubmitApplication,
  }) => {
    const { company, companyUser } = moduleData.verificationData.viewer;
    const { lastKycReview } = companyUser;
    const { companyProfileId, lastKybReview } = company;

    const steps = getVerificationSteps(moduleData);

    const hasInvalidSteps = steps.some(
      ({ status }) => status === VerificationStatus.Invalid,
    );

    const numberOfRemainingSteps = steps.filter(
      ({ status }) =>
        status !== VerificationStatus.Completed &&
        status !== VerificationStatus.Validated,
    ).length;

    const areAllStepsCompleted = numberOfRemainingSteps === 0;

    const isFirstSubmission = !lastKycReview && !lastKybReview;

    // This polling is used to handle the auto-fetching of the statutes.
    // If we succeed to download them in the background, its status will instantly
    // be changed to COMPLETED to avoid confusing the user.
    useQuery(PollVerificationModuleKybDocumentsDocument, {
      pollInterval: 2000,
      skip: !steps.some(
        ({ id, status }) =>
          [State.STATUTES, State.COMPANY_PAPERS].includes(id) &&
          status === VerificationStatus.ToDo,
      ),
      variables: {
        companyProfileId,
      },
    });

    return (
      <Layout
        data-testid="onboarding-hub"
        nav={
          <OBNavigation
            onPrev={
              moduleData.applicantType === ApplicantType.Mandatee
                ? onPrev
                : undefined
            }
          />
        }
        variant="larger"
      >
        <Box paddingBottom="space-64">
          <Header title={locales.title} />

          {/* We only display the error message if at least one step is invalid.
          If the user has no action to perform, displaying it is confusing.
          It means after an invalid review, fixing all the errors makes the error disapear. */}
          {lastKycReview?.error && !areAllStepsCompleted ? (
            <Callout marginY="space-40" variant="warning">
              <Callout.Content>{lastKycReview.error}</Callout.Content>
            </Callout>
          ) : null}

          <SunshineCard marginBottom="space-40" padding="space-24">
            <HStack gap="space-8" justifyContent="space-between">
              <HStack alignItems="start" gap="space-16">
                <SquircleIcon colorScheme="grey" name="store" />
                <VStack align="stretch" gap="space-4">
                  <Text fontWeight="weight-500" size="md" variant="primary">
                    {company.profile.legalName}
                  </Text>

                  <Text size="sm" variant="secondary">
                    {company.profile.legalForm}
                  </Text>
                  <Text size="sm" variant="secondary">
                    {formatActivityType(company.profile.activityType)}
                  </Text>
                </VStack>
              </HStack>

              <Button
                alignSelf="right"
                iconPosition="right"
                onClick={onGeneralInformationClick}
                variant="secondary"
              >
                {locales.generalInfo}
              </Button>
            </HStack>
          </SunshineCard>

          <VStack align="stretch" gap="space-4" marginBottom="space-16">
            <Text fontWeight="weight-500" size="md" variant="primary">
              {locales.toComplete}
            </Text>
            <Text size="sm" variant="secondary">
              {hasInvalidSteps ? locales.errorSubtitle : locales.subtitle}
            </Text>
          </VStack>

          <VStack gap="space-16">
            {[
              VerificationStepCategory.PersonalInfo,
              VerificationStepCategory.CompanyInfo,
              VerificationStepCategory.ShineOffer,
            ].map((category) => (
              <Category
                category={category}
                displayOnlyRemainingSteps={!isFirstSubmission}
                key={category}
                onStepClick={onStepClick}
                verificationSteps={steps}
              />
            ))}
          </VStack>

          {/* As we only show invalid steps when it's not the 1st submission,
            we'll display some text when they've all be completed and there's no step to display */}
          {!isFirstSubmission && areAllStepsCompleted ? (
            <SunshineCard alignItems="center">
              <SunshineCard.Slot name="aside">
                <SquircleIcon colorScheme="green" name="tick" />
              </SunshineCard.Slot>
              <SunshineCard.Content>
                <SunshineCard.Title>
                  {locales.applicationCompleted.title}
                </SunshineCard.Title>
                <SunshineCard.Text>
                  {locales.applicationCompleted.subtitle}
                </SunshineCard.Text>
              </SunshineCard.Content>
            </SunshineCard>
          ) : null}

          <Flex justify="flex-end">
            <Button
              alignSelf="right"
              icon="arrow-right"
              iconPosition="right"
              iconSpacing="space-4"
              isDisabled={!areAllStepsCompleted}
              marginTop="space-48"
              onClick={onSubmitApplication}
            >
              {isFirstSubmission
                ? locales.submitApplication.send
                : locales.submitApplication.resend}
            </Button>
          </Flex>
        </Box>
      </Layout>
    );
  },
);

export default OnboardingHub;
